import type { ProductVendor } from "@models/product";
import { currencyFormatter } from "@utils/formatter";

// What a refused line can do next (#443, PR 326 round 3): leave (its product or offering
// is gone), move to another vendor that can take the whole line, or — when that vendor is
// out of reach only by the per-line limit — lower that vendor's line first (PR 345 review),
// with leaving as the second choice.
export type LineRefusalAction =
    | { kind: "remove"; onRemove: () => void }
    | {
        kind: "switch";
        vendor: ProductVendor;
        onSwitch: (vendor: ProductVendor) => void;
        disabled: boolean;
    }
    | {
        kind: "lowerToSwitch";
        vendor: ProductVendor;
        lowerTo: number;
        onRemove: () => void;
    };

interface LineRefusalNoticeProps {
    message: string;
    action?: LineRefusalAction;
}

// A cart line's refused write — a quantity change or a vendor switch — said on the line,
// with the one thing the shopper can do about it, if there is one.
export const LineRefusalNotice = ({ message, action }: LineRefusalNoticeProps) => (
    <>
        <p
            role="alert"
            data-testid="cart-item-refusal"
            className="text-sm font-semibold text-error"
        >
            {message}
        </p>
        {action?.kind === "lowerToSwitch" && (
            <p data-testid="cart-item-next-step" className="text-sm text-neutral">
                {`Lower your ${action.vendor.vendorName} line to ${action.lowerTo} to switch this one`}
            </p>
        )}
        {(action?.kind === "remove" || action?.kind === "lowerToSwitch") && (
            <button
                type="button"
                onClick={action.onRemove}
                className="btn btn-sm btn-outline btn-error self-start"
            >
                Remove this item
            </button>
        )}
        {action?.kind === "switch" && (
            <button
                type="button"
                onClick={() => action.onSwitch(action.vendor)}
                disabled={action.disabled}
                className="btn btn-sm btn-outline btn-primary self-start"
            >
                {`Switch to ${action.vendor.vendorName} (${currencyFormatter.format(action.vendor.price)})`}
            </button>
        )}
    </>
);
